import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import stripeService from '../services/stripeService';
import usageTracker from '../services/usageTracker';
import FavoriteMealsList from './FavoriteMealsList';
import supabase from '../supabase/supabase';

const { FiUser, FiMail, FiLock, FiEye, FiEyeOff, FiEdit2, FiSave, FiX, FiStar, FiZap, FiCreditCard, FiCalendar, FiDollarSign, FiRefreshCw, FiLogOut, FiAlertCircle, FiCheck, FiInfo, FiHeart, FiSettings, FiToggleLeft, FiToggleRight } = FiIcons;

const UserProfile = ({ user, onUserUpdate, onClose, onLoadFavorite, onEditProfile }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    enableSmartSuggestions: true
  });
  
  const [errors, setErrors] = useState({});
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [userUsage, setUserUsage] = useState(null);
  const [isLoadingUsage, setIsLoadingUsage] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        email: user.email || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        enableSmartSuggestions: user.enable_smart_suggestions !== false
      });
      loadUsageStats();
    }
  }, [user]);

  const loadUsageStats = async () => {
    setIsLoadingUsage(true);
    try {
      const usage = await usageTracker.getCurrentUserUsage();
      setUserUsage(usage);
    } catch (error) {
      console.error('Error loading usage stats:', error);
    } finally {
      setIsLoadingUsage(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    // For the toggle, we now handle it separately, but keeping this for other inputs
    const finalValue = type === 'checkbox' ? checked : value;
    
    setFormData((prev) => ({
      ...prev,
      [name]: finalValue
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSmartSuggestionsToggle = async (e) => {
    const newValue = e.target.checked;
    
    // 1. Optimistic Update
    setFormData(prev => ({ ...prev, enableSmartSuggestions: newValue }));

    // 2. Immediate DB Update
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ enable_smart_suggestions: newValue })
        .eq('id', user.id);

      if (error) throw error;

      // 3. Update parent/global user state
      onUserUpdate({ ...user, enable_smart_suggestions: newValue });

    } catch (error) {
      console.error('Error updating smart suggestions:', error);
      // Revert on error
      setFormData(prev => ({ ...prev, enableSmartSuggestions: !newValue }));
      setErrors({ general: 'Failed to update setting. Please try again.' });
    }
  };

  const validateForm = () => {
    // Basic validation logic
    if (formData.newPassword && formData.newPassword !== formData.confirmPassword) {
      setErrors(prev => ({ ...prev, confirmPassword: "Passwords don't match" }));
      return false;
    }
    return true;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSaving(true);
    setErrors({});

    try {
      // 1. Update Profile Data (Names only, suggestions are live-saved)
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          first_name: formData.firstName,
          last_name: formData.lastName
        })
        .eq('id', user.id);

      if (profileError) throw profileError;

      // 2. Update Auth Data (Email/Password) if changed
      const authUpdates = {};
      if (formData.email !== user.email) {
        authUpdates.email = formData.email;
      }
      if (formData.newPassword) {
        authUpdates.password = formData.newPassword;
      }

      if (Object.keys(authUpdates).length > 0) {
        const { data: updatedUser, error: authError } = await supabase.auth.updateUser(authUpdates);
        if (authError) throw authError;
      } 
      
      // 3. Refresh user data to update parent state
      const { data: { session } } = await supabase.auth.getSession();
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();
        
      onUserUpdate({ ...session.user, ...profile });
      setIsEditing(false);

    } catch (error) {
      console.error('Error updating profile:', error);
      setErrors({ general: 'Failed to update profile. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpgradeClick = () => {
    stripeService.openCheckoutPage();
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    onClose();
  };

  const getSubscriptionStatus = () => {
    if (!userUsage) return { text: 'Loading...', color: 'text-gray-600', bgColor: 'bg-gray-100' };
    if (userUsage.has_paid) {
      return {
        status: 'active',
        text: 'Active Subscription',
        color: 'text-green-600',
        bgColor: 'bg-green-100',
      };
    }
    return {
      status: 'free',
      text: 'Free Trial',
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
    };
  };

  const getRemainingCalculations = () => {
    if (!userUsage) return 'Loading...';
    if (userUsage.has_paid) {
      return 'Unlimited';
    }
    const remaining = Math.max(0, 3 - userUsage.successful_calculations);
    return `${remaining} left`;
  };

  const subscriptionStatus = getSubscriptionStatus();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <SafeIcon icon={FiUser} className="text-indigo-600" />
            User Profile
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
          >
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>

        <div className="flex flex-col md:flex-row flex-grow min-h-0">
          {/* Sidebar */}
          <div className="md:w-1/3 p-6 border-r border-gray-200 overflow-y-auto bg-gray-50">
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3 border-2 border-indigo-200">
                <SafeIcon icon={FiUser} className="text-3xl text-indigo-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">
                {user?.first_name} {user?.last_name}
              </h3>
              <p className="text-gray-600 text-sm">{user?.email}</p>
            </div>

            <div className="space-y-2">
              <button
                onClick={() => setActiveTab('profile')}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                  activeTab === 'profile'
                    ? 'bg-white shadow-sm text-indigo-800 font-medium border border-gray-200'
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                <SafeIcon icon={FiUser} className="text-lg" />
                <span>Profile Information</span>
              </button>
              <button
                onClick={() => setActiveTab('subscription')}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                  activeTab === 'subscription'
                    ? 'bg-white shadow-sm text-indigo-800 font-medium border border-gray-200'
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                <SafeIcon icon={FiStar} className="text-lg" />
                <span>Subscription</span>
              </button>
              <button
                onClick={() => setActiveTab('favorites')}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                  activeTab === 'favorites'
                    ? 'bg-white shadow-sm text-indigo-800 font-medium border border-gray-200'
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                <SafeIcon icon={FiHeart} className="text-lg" />
                <span>Favorite Meals</span>
              </button>
              <button
                onClick={onEditProfile}
                className="w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 text-gray-600 hover:bg-gray-200"
              >
                <SafeIcon icon={FiEdit2} className="text-lg" />
                <span>Edit Physical Profile</span>
              </button>
              <button
                onClick={handleSignOut}
                className="w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 text-red-600 hover:bg-red-50 mt-4"
              >
                <SafeIcon icon={FiLogOut} className="text-lg" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>

          {/* Content Area */}
          <div className="md:w-2/3 p-6 overflow-y-auto">
            {activeTab === 'favorites' && <FavoriteMealsList onLoadFavorite={onLoadFavorite} />}

            {activeTab === 'profile' && (
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-6">Profile Information</h3>
                
                {errors.general && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center gap-2 text-red-700">
                    <SafeIcon icon={FiAlertCircle} className="text-red-500 flex-shrink-0" />
                    <span className="text-sm">{errors.general}</span>
                  </div>
                )}

                <form onSubmit={handleSave} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                      {isEditing ? (
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${errors.firstName ? 'border-red-300' : 'border-gray-300'}`}
                          placeholder="John"
                        />
                      ) : (
                        <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-800 border border-gray-200">{user?.first_name || 'Not set'}</div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                      {isEditing ? (
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${errors.lastName ? 'border-red-300' : 'border-gray-300'}`}
                          placeholder="Doe"
                        />
                      ) : (
                        <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-800 border border-gray-200">{user?.last_name || 'Not set'}</div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                    {isEditing ? (
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${errors.email ? 'border-red-300' : 'border-gray-300'}`}
                        placeholder="john@example.com"
                      />
                    ) : (
                      <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-800 border border-gray-200">{user?.email || 'Not set'}</div>
                    )}
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>

                  {/* Smart Suggestions Setting - Always Active/Toggleable */}
                  <div className="pt-4 mt-2">
                    <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                       <SafeIcon icon={FiZap} className="text-indigo-600" />
                       Settings
                    </h4>
                    <div className="p-4 rounded-xl border bg-gray-50 border-gray-200 transition-colors hover:border-indigo-200">
                      <div className="flex items-center justify-between">
                        <div className="pr-4">
                          <label htmlFor="smart-suggestions-toggle" className="font-semibold text-gray-800 mb-1 block cursor-pointer">
                            Smart Food Suggestions
                          </label>
                          <p className="text-sm text-gray-600">
                            Automatically suggest adding foods or extending ranges when your selection can't meet targets.
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input 
                              type="checkbox" 
                              name="enableSmartSuggestions"
                              id="smart-suggestions-toggle" 
                              className="sr-only peer"
                              checked={formData.enableSmartSuggestions}
                              onChange={handleSmartSuggestionsToggle}
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {isEditing && (
                    <>
                      <div className="border-t border-gray-200 pt-4 mt-6">
                        <h4 className="font-medium text-gray-800 mb-3">Change Password</h4>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                            <div className="relative">
                              <input
                                type={showNewPassword ? 'text' : 'password'}
                                name="newPassword"
                                value={formData.newPassword}
                                onChange={handleInputChange}
                                className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${errors.newPassword ? 'border-red-300' : 'border-gray-300'}`}
                                placeholder="Leave blank to keep current password"
                              />
                              <button
                                type="button"
                                onClick={() => setShowNewPassword(!showNewPassword)}
                                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                              >
                                <SafeIcon icon={showNewPassword ? FiEyeOff : FiEye} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setIsEditing(false)}
                          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isSaving}
                          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white rounded-lg transition-colors flex items-center gap-2"
                        >
                          {isSaving ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                              <span>Saving...</span>
                            </>
                          ) : (
                            <>
                              <SafeIcon icon={FiSave} />
                              <span>Save Changes</span>
                            </>
                          )}
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setIsEditing(true)}
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors flex items-center gap-2"
                      >
                        <SafeIcon icon={FiEdit2} />
                        <span>Edit Profile</span>
                      </button>
                    )}
                  </div>
                </form>
              </div>
            )}

            {activeTab === 'subscription' && (
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-6">Subscription</h3>
                <div className="space-y-6">
                  <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="font-bold text-gray-800">Subscription Status</h4>
                        <p className="text-gray-600">Manage your subscription plan</p>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-sm font-medium ${subscriptionStatus.bgColor} ${subscriptionStatus.color}`}>
                        {subscriptionStatus.text}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                        <div className="flex items-center gap-2 mb-2">
                          <SafeIcon icon={FiZap} className="text-orange-500" />
                          <span className="font-medium text-gray-700">Meal Calculations</span>
                        </div>
                        <div className="text-2xl font-bold text-gray-800">
                          {isLoadingUsage ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-indigo-600 border-t-transparent" />
                          ) : (
                            getRemainingCalculations()
                          )}
                        </div>
                        <p className="text-sm text-gray-500">This month</p>
                      </div>
                      
                      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                        <div className="flex items-center gap-2 mb-2">
                          <SafeIcon icon={FiCalendar} className="text-blue-500" />
                          <span className="font-medium text-gray-700">Member Since</span>
                        </div>
                        <div className="text-lg font-bold text-gray-800">
                          {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-100">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <h4 className="font-bold text-gray-800 text-lg">Meal Calculator Pro</h4>
                        <p className="text-gray-600">
                          {userUsage?.has_paid ? 'Enjoy unlimited access to premium features' : 'Unlock unlimited meal calculations and premium features'}
                        </p>
                      </div>
                      <div className="flex flex-col items-end">
                        <div className="text-2xl font-bold text-indigo-600 mb-1">
                          $9.99<span className="text-sm font-normal text-gray-500">/month</span>
                        </div>
                        {userUsage?.has_paid ? (
                          <div className="text-sm text-gray-500">
                            Next billing: {' '}
                            {userUsage?.payment_date ? new Date(userUsage.payment_date).toLocaleDateString() : 'N/A'}
                          </div>
                        ) : (
                          <button
                            onClick={handleUpgradeClick}
                            className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                          >
                            <SafeIcon icon={FiStar} />
                            <span>Upgrade to Pro</span>
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2"><SafeIcon icon={FiCheck} className="text-green-500" /><span className="text-gray-700">Unlimited meal calculations</span></div>
                        <div className="flex items-center gap-2"><SafeIcon icon={FiCheck} className="text-green-500" /><span className="text-gray-700">Access to 500,000+ foods</span></div>
                        <div className="flex items-center gap-2"><SafeIcon icon={FiCheck} className="text-green-500" /><span className="text-gray-700">Advanced macro optimization</span></div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2"><SafeIcon icon={FiCheck} className="text-green-500" /><span className="text-gray-700">Custom food creation</span></div>
                        <div className="flex items-center gap-2"><SafeIcon icon={FiCheck} className="text-green-500" /><span className="text-gray-700">Export meal plans as PDF</span></div>
                        <div className="flex items-center gap-2"><SafeIcon icon={FiCheck} className="text-green-500" /><span className="text-gray-700">Priority support</span></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default UserProfile;